import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Calendar, Stethoscope, GraduationCap, Brain, FileText, Heart, Phone } from '../components/Icons.jsx';

const stages = [
  {
    id: 'year10-11',
    when: 'Year 10–11',
    band: 'Pre-A-level',
    title: 'GCSEs — quietly the biggest filter',
    intro: 'Most med schools score GCSEs as part of their academic filter. Some (Birmingham, Cardiff, Sheffield, Manchester) weight them heavily. Strong GCSEs widen your school choice later — weak ones close doors before UCAT even matters.',
    steps: [
      'Target 7s and above in all sciences, English and maths (8s/9s preferred for Russell Group).',
      'Some schools count number of 7+ grades — get to 7 sevens minimum, ideally 8–9.',
      'Start visible volunteering — care home, hospice befriender, St John cadets. This compounds across two years.',
      'No formal work experience needed yet — but observe one or two doctor relatives / family friends if you can.'
    ],
    tool: { to: '/we-quiz', label: 'Score your work experience so far' }
  },
  {
    id: 'year12-start',
    when: 'Year 12 — Sept–Mar',
    band: 'A-level year 1',
    title: 'Lock in subjects and start the work-experience clock',
    intro: 'Most med schools require Chemistry A-level. Biology is required by most, but a small group accept it at GCSE only. A strong third subject can be anything academic — Maths and Physics keep more doors open than Psychology or Sociology.',
    steps: [
      'Take Chemistry A-level. Take Biology unless you have a specific reason not to.',
      'Predicted A*AA or better keeps every option open. AAA closes Oxford, Cambridge, UCL, KCL, Queen Mary, Brighton & Sussex.',
      'Book at least one in-person placement for the summer. Career Camp is one option — apply early, capped cohorts.',
      'Set aside time weekly for longitudinal volunteering — admissions tutors love sustained commitment.'
    ],
    tool: { to: '/grade-check', label: 'Check grade realism' }
  },
  {
    id: 'year12-summer',
    when: 'Year 12 summer',
    band: 'Pre-UCAT',
    title: 'UCAT prep + the work-experience week',
    intro: 'This is the highest-leverage window of your application. UCAT prep peaks here. In-person work experience happens here. Personal statement starts here.',
    steps: [
      'Book your UCAT slot July–September. Earlier sittings give you a re-take buffer only if you book a second slot.',
      'Do the Career Camp week (London, Birmingham or Manchester) or equivalent — 5 days of real NHS exposure.',
      'Use the work-experience reflection tool to convert each day into a paragraph mapped to a GMC outcome.',
      'Start the personal statement first draft. Target 4000 characters / 47 lines.',
      'Decide: BMAT was withdrawn after the 2023 sitting. You only need UCAT now — but the Cambridge graduate-entry route uses GAMSAT (see GAMSAT guide).'
    ],
    tool: { to: '/work-experience', label: 'See Career Camp' }
  },
  {
    id: 'year13-autumn',
    when: 'Year 13 — Sept–15 Oct',
    band: 'UCAS deadline',
    title: 'UCAS deadline · 15 October',
    intro: 'Hard deadline. Medicine, dentistry and veterinary applications close 15 October — three months earlier than the standard UCAS deadline. You pick 4 medicine choices + 1 non-medicine backup.',
    steps: [
      'Final UCAT score lands a few days after you sit it. Match it against the Where Can I Apply tool to pick 4 medicine choices.',
      'Mix the four: 1 threshold school (ranked by UCAT), 2 holistic (UCAT used loosely), 1 lower-UCAT school as safety.',
      'Personal statement final version uploaded.',
      'References from your school submitted via UCAS.',
      'Pay the UCAS fee. Done.'
    ],
    tool: { to: '/where-can-i-apply', label: 'Pick your 4 schools' }
  },
  {
    id: 'interviews',
    when: 'Year 13 — Nov–Mar',
    band: 'Interview season',
    title: 'MMI and panel interviews',
    intro: 'Most UK med schools interview by Multiple Mini Interviews (MMI) — 6 to 10 short stations, each on a different scenario or topic. A few still run panel interviews. NHS hot topics, ethics, motivation, communication and station-style scenarios all come up.',
    steps: [
      'Practice MMI stations weekly from November. Common stations: ethical scenarios, role-play, data interpretation, motivation, NHS hot topic.',
      'Use the NHS Hot Topics resource to stay current — interviewers test how recent your reading is.',
      'Read your personal statement back like an interviewer would — every claim is a possible question.',
      'Mock interviews with a Medic Mind tutor close the gap between "I know this stuff" and "I can perform this under pressure".'
    ],
    tool: { to: '/hot-topics', label: 'NHS hot topics' }
  },
  {
    id: 'offers',
    when: 'Year 13 — Feb–May',
    band: 'Offers in',
    title: 'Offers, firms and insurance',
    intro: 'Most offers land between February and May. You hold up to two — one firm, one insurance. The insurance has to be lower-grade than the firm.',
    steps: [
      'Don\'t firm an offer until you have at least one solid option in hand.',
      'Insurance choice: pick an offer with grades you\'re confident you\'ll hit even on a bad results day.',
      'No offers? Apply for UCAS Extra in March (one more medicine choice if any have spaces). Otherwise plan re-application route.',
      'Results day August: confirm or clear. Medicine is rarely in Clearing — plan around a confirmed offer or a re-application gap year.'
    ],
    tool: { to: '/medi', label: 'Ask Ash about firm vs insurance' }
  },
  {
    id: 'gap-or-grad',
    when: 'After Year 13',
    band: 'Alternative routes',
    title: 'Gap year, re-application, or graduate-entry',
    intro: 'If you don\'t get an offer, the three viable routes are: re-applying with a stronger UCAT and an extra year of work experience, taking a different undergraduate degree and applying via graduate-entry medicine (GAMSAT), or going abroad.',
    steps: [
      'Re-application route: take a productive gap year — clinical job, longer work experience, paid healthcare role. Re-take UCAT only if it was the bottleneck.',
      'Graduate-entry route: 3-year health-related degree (or anything in a few unis) → GAMSAT → apply to Cambridge / Nottingham / Swansea / Liverpool / Plymouth GEM courses. 4-year accelerated medicine.',
      'See our GAMSAT guide for the graduate-entry route in full.',
      'Abroad route: most students who go abroad come back via the PLAB pathway. Worth it for some, expensive for many.'
    ],
    tool: { to: '/gamsat', label: 'GAMSAT guide' }
  }
];

export default function Timeline() {
  const [openId, setOpenId] = useState(stages[2].id);

  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Free guide</div>
        <h1>Everything you need to know about applying for medicine</h1>
        <p className="subtitle">A full timeline — GCSEs to graduate-entry — with the milestones, the Medic Mind tools to use at each stage, and the things that quietly disqualify applicants if you leave them too late. Tap a stage to expand.</p>
      </div>

      <section className="card" style={{
        background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
        color: '#fff', border: 'none', marginBottom: 24, padding: '22px 26px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ width: 48, height: 48, background: 'rgba(255,255,255,0.15)', borderRadius: 12, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Calendar width="24" height="24" />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>The one date you must not miss</h3>
            <p style={{ margin: 0, color: '#e9d5ff', fontSize: 15 }}><strong>15 October</strong> — UCAS deadline for medicine, dentistry and veterinary. Three months earlier than every other degree.</p>
          </div>
        </div>
      </section>

      <div style={{ position: 'relative' }}>
        <div className="timeline-rail" />
        {stages.map((s, i) => (
          <StageCard key={s.id} stage={s} index={i} isOpen={openId === s.id} onToggle={() => setOpenId(openId === s.id ? null : s.id)} />
        ))}
      </div>

      <section className="parents-cta" style={{ marginTop: 28 }}>
        <div>
          <h2>Not sure where you are on the timeline?</h2>
          <p>A free 15-min call with a Medic Mind advisor maps where you are vs where you need to be — UCAT score, work experience, school choice, predicted grades — in one go.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free call <ArrowRight width="16" height="16" /></a>
      </section>

      <style>{`
        .timeline-rail {
          position: absolute;
          left: 22px; top: 22px; bottom: 22px;
          width: 2px;
          background: var(--border);
          z-index: 0;
        }
        @media (max-width: 720px) { .timeline-rail { display: none; } }
      `}</style>
    </>
  );
}

function StageCard({ stage, index, isOpen, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 14, position: 'relative', zIndex: 1 }}>
      <div style={{
        flexShrink: 0, width: 46, height: 46,
        borderRadius: '50%',
        background: isOpen ? 'var(--brand-purple)' : '#fff',
        border: `2px solid ${isOpen ? 'var(--brand-purple)' : 'var(--border-strong)'}`,
        color: isOpen ? '#fff' : 'var(--text-2)',
        display: 'grid', placeItems: 'center',
        fontWeight: 700, fontSize: 14, marginTop: 4,
        transition: 'all 180ms ease'
      }}>{index + 1}</div>
      <div
        onClick={onToggle}
        style={{
          flex: 1,
          background: 'var(--card)',
          border: `1px solid ${isOpen ? 'var(--brand-purple)' : 'var(--border)'}`,
          borderRadius: 14,
          padding: '18px 22px',
          cursor: 'pointer',
          transition: 'border-color 180ms ease, box-shadow 180ms ease',
          boxShadow: isOpen ? 'var(--shadow-card-hover)' : 'var(--shadow-card)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{stage.when} · {stage.band}</div>
            <h3 style={{ margin: '6px 0 0', fontSize: 19, color: 'var(--text)' }}>{stage.title}</h3>
          </div>
          <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>{isOpen ? '−' : '+'}</span>
        </div>

        {isOpen && (
          <div style={{ marginTop: 14, animation: 'fadeUp 220ms ease' }}>
            <p style={{ color: 'var(--text-2)', margin: '0 0 14px', fontSize: 14.5, lineHeight: 1.6 }}>{stage.intro}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stage.steps.map((step) => (
                <li key={step} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: 'var(--text)' }}>
                  <span style={{ color: 'var(--brand-purple)', flexShrink: 0, marginTop: 4 }}>▸</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
            {stage.tool && (
              <Link to={stage.tool.to} className="btn btn-primary" onClick={(e) => e.stopPropagation()} style={{ fontSize: 14 }}>
                {stage.tool.label} <ArrowRight width="14" height="14" />
              </Link>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
